import React, { useCallback, useMemo, useState } from "react"
import {
  getCountryCallingCode,
  parsePhoneNumber,
  type Country,
} from "react-phone-number-input"

import {
  Select as CovalSelect,
  SelectItem as CovalSelectItem,
  Input,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import * as Flags from "country-flag-icons/react/3x2"

interface PhoneNumberInputProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  className?: string
  error?: string
}

const COMMON_COUNTRIES: ReadonlyArray<{ code: Country; name: string }> = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "CO", name: "Colombia" },
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
  { code: "IN", name: "India" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "BR", name: "Brazil" },
  { code: "MX", name: "Mexico" },
  { code: "JP", name: "Japan" },
  { code: "CN", name: "China" },
] as const

const COUNTRY_CODE_MAP: Record<string, Country> = {
  "1": "US",
  "33": "FR",
  "34": "ES",
  "39": "IT",
  "44": "GB",
  "49": "DE",
  "52": "MX",
  "55": "BR",
  "57": "CO",
  "61": "AU",
  "81": "JP",
  "86": "CN",
}

const Flag = ({ code }: { code: string }) => {
  const FlagComponent = Flags[code as keyof typeof Flags]
  if (!FlagComponent) {
    return null
  }
  return <FlagComponent className="w-5 h-auto shrink-0" />
}

const detectCountryFromValue = (phoneValue: string): Country => {
  if (!phoneValue) {
    return "US"
  }

  try {
    const parsed = parsePhoneNumber(phoneValue)
    if (parsed?.country) {
      return parsed.country
    }
  } catch {
    const match = phoneValue.match(/^\+?(\d{1,3})/)
    if (match) {
      const code = match[1]
      for (const [countryCode, country] of Object.entries(COUNTRY_CODE_MAP)) {
        if (code.startsWith(countryCode)) {
          return country
        }
      }
    }
  }

  return "US"
}

const stripCountryCode = (phoneValue: string, country: Country): string => {
  if (!phoneValue) {
    return ""
  }

  const digitsOnly = phoneValue.replace(/[^\d]/g, "")
  const countryCode = getCountryCallingCode(country)

  if (digitsOnly.startsWith(countryCode)) {
    return digitsOnly.slice(countryCode.length)
  }

  return digitsOnly
}

export const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  value,
  onChange,
  disabled = false,
  error,
  className = "",
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const [manuallySelectedCountry, setManuallySelectedCountry] =
    useState<Country | null>(null)

  const detectedCountry = useMemo(() => detectCountryFromValue(value), [value])
  const selectedCountry = manuallySelectedCountry || detectedCountry

  const countryCode = useMemo(
    () => getCountryCallingCode(selectedCountry),
    [selectedCountry],
  )

  const displayValue = useMemo(
    () => stripCountryCode(value, selectedCountry),
    [value, selectedCountry],
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value.replace(/[^\d]/g, "")
      const formatted = inputValue ? `+${countryCode}${inputValue}` : ""
      onChange(formatted)
    },
    [onChange, countryCode],
  )

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData("text").trim()
      if (!pastedText) {
        return
      }

      // With '+': parse as international; may auto-switch the country.
      if (pastedText.startsWith("+")) {
        try {
          const parsed = parsePhoneNumber(pastedText)
          if (parsed?.country && parsed.number) {
            e.preventDefault()
            if (parsed.country !== selectedCountry) {
              setManuallySelectedCountry(parsed.country)
            }
            onChange(parsed.number)
          }
        } catch {
          // Fall through to handleChange.
        }
        return
      }

      // Without '+': only strip a leading country code when the digits start
      // with the *currently selected* country's calling code AND the result
      // is a valid national number for that country. This avoids hijacking
      // legitimate national pastes like "(646) 290-1067".
      const digitsOnly = pastedText.replace(/[^\d]/g, "")
      if (!digitsOnly.startsWith(countryCode)) {
        return
      }

      try {
        const parsed = parsePhoneNumber(`+${digitsOnly}`)
        if (
          parsed?.country === selectedCountry &&
          parsed.number &&
          parsed.isValid()
        ) {
          e.preventDefault()
          onChange(parsed.number)
        }
      } catch {
        // Fall through to handleChange.
      }
    },
    [onChange, selectedCountry, countryCode],
  )

  const handleCountryChange = useCallback(
    (newCountry: string) => {
      const country = newCountry as Country
      setManuallySelectedCountry(country)
      const newCode = getCountryCallingCode(country)
      const nationalNumber = stripCountryCode(value, selectedCountry)
      onChange(nationalNumber ? `+${newCode}${nationalNumber}` : "")
    },
    [onChange, value, selectedCountry],
  )

  const handleFocus = useCallback(() => setIsFocused(true), [])
  const handleBlur = useCallback(() => setIsFocused(false), [])

  const showError = !isFocused && value.length > 0 && !!error

  return (
    <div className={`w-full ${className}`}>
      <div className="flex gap-2">
        <div className="w-28 shrink-0">
          <CovalSelect
            value={selectedCountry}
            onValueChange={handleCountryChange}
            disabled={disabled}
          >
            <SelectTrigger aria-label="Country code">
              <SelectValue>
                <span className="flex items-center gap-1.5">
                  <Flag code={selectedCountry} />
                  <span>+{countryCode}</span>
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="w-48">
              {COMMON_COUNTRIES.map((country) => (
                <CovalSelectItem
                  key={country.code}
                  value={country.code}
                  textValue={`+${getCountryCallingCode(country.code)}`}
                >
                  <span className="flex items-center gap-2">
                    <Flag code={country.code} />
                    <span className="flex-1">{country.name}</span>
                    <span className="text-coval-text-muted">
                      +{getCountryCallingCode(country.code)}
                    </span>
                  </span>
                </CovalSelectItem>
              ))}
            </SelectContent>
          </CovalSelect>
        </div>
        <div className="flex-1">
          <Input
            type="tel"
            value={displayValue}
            onChange={handleChange}
            onPaste={handlePaste}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder="234 567 8901"
            error={showError}
            aria-label="Phone number"
          />
        </div>
      </div>
      {showError && (
        <p className="text-xs text-coval-danger-text mt-1">
          Please enter a valid phone number
        </p>
      )}
    </div>
  )
}
